import { notifyError } from '@/lib/query'
import { cn } from '@/lib/utils'
import { type ClassifyMatch, type ClassifyStats, sendFeedback } from '@/lib/workflows'
import { ExternalLink, Loader2, ThumbsDown, ThumbsUp } from 'lucide-react'
import { useState } from 'react'

/** What a smart check found, in the chat. Right / Wrong on each result teaches the check. */
export function ClassifyMatches({
  messageId,
  data
}: {
  messageId: number
  data: { workflow: number; node: string; label: string; items: ClassifyMatch[]; stats: ClassifyStats }
}) {
  const [verdicts, setVerdicts] = useState<Record<string, boolean | undefined>>(() =>
    Object.fromEntries(data.items.map((it) => [String(it.id), it.verdict]))
  )
  const [pending, setPending] = useState<string | null>(null)
  const s = data.stats

  async function judge(item: ClassifyMatch, match: boolean) {
    const key = String(item.id)
    setPending(key)
    try {
      await sendFeedback(data.workflow, {
        node: data.node,
        item: { id: item.id, text: item.text },
        match,
        message: messageId
      })
      setVerdicts((v) => ({ ...v, [key]: match }))
    } catch (err) {
      notifyError('Could not save that', err)
    } finally {
      setPending(null)
    }
  }

  return (
    <div className="space-y-2">
      <div className="rounded-[24px] border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
        <p className="text-sm font-bold text-neutral-900 dark:text-white">
          {data.label}: {data.items.length} found
        </p>
        <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
          Checked {s.checked} · {s.by_keyword} by keywords · {s.by_meaning} by meaning · AI looked at{' '}
          {s.to_ai}
          {s.second_opinion ? ` (${s.second_opinion} double-checked)` : ''} · $
          {s.cost_usd.toFixed(4)}
        </p>
      </div>
      {data.items.map((it) => {
        const key = String(it.id)
        const verdict = verdicts[key]
        return (
          <div
            key={key}
            className={cn(
              'rounded-[24px] border bg-white p-4 transition-colors dark:bg-neutral-900',
              verdict === false
                ? 'border-neutral-200 opacity-60 dark:border-neutral-800'
                : 'border-neutral-200 dark:border-neutral-800'
            )}
          >
            <p className="line-clamp-3 whitespace-pre-wrap break-words text-sm text-neutral-800 dark:text-neutral-200">
              {it.text}
            </p>
            <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
              <span className="font-semibold text-neutral-700 dark:text-neutral-300">Why:</span> {it.why}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
                {Math.round(it.confidence * 100)}% sure
              </span>
              {it.link && (
                <a
                  href={it.link}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
                >
                  <ExternalLink className="size-3" /> Open post
                </a>
              )}
              <div className="ml-auto flex items-center gap-1">
                {pending === key && <Loader2 className="size-3.5 animate-spin text-neutral-400" />}
                {(
                  [
                    [true, 'Right', ThumbsUp],
                    [false, 'Wrong', ThumbsDown]
                  ] as const
                ).map(([match, text, Icon]) => (
                  <button
                    key={text}
                    type="button"
                    aria-pressed={verdict === match}
                    disabled={pending !== null}
                    onClick={() => judge(it, match)}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold transition-all active:scale-95 disabled:opacity-50',
                      verdict === match
                        ? match
                          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400'
                          : 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400'
                        : 'text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800'
                    )}
                  >
                    <Icon className="size-3.5" />
                    {text}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
